import { useState, useEffect } from "react";

// ─── Dark Mode Sync ───────────────────────────────────────────────────────────
// Reads the same state the Header writes:
//   localStorage["trw-dark-mode"] === "1" | "0"
//   document.documentElement classList "trw-dark-mode"
//   document.body classList "trw-dark-mode" | "dark"

function getHeaderDarkMode() {
  if (typeof window === "undefined") return false;
  const stored = window.localStorage.getItem("trw-dark-mode");
  if (stored === "1") return true;
  if (stored === "0") return false;
  return (
    document.documentElement.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("trw-dark-mode") ||
    document.body.classList.contains("dark")
  );
}

function useSyncedDarkMode() {
  const [isDark, setIsDark] = useState(getHeaderDarkMode);

  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(getHeaderDarkMode()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observer.observe(document.body, { attributes: true, attributeFilter: ["class", "style"] });

    const onStorage = (e) => {
      if (e.key === "trw-dark-mode") setIsDark(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return isDark;
}

// ─── Rich text helper ─────────────────────────────────────────────────────────
// Instead of JSX fragments in module-level arrays (rejected by OXC),
// we store content as plain strings with a simple bold marker: **text**
// The Rich component renders them inline.

function Rich({ children }) {
  if (typeof children !== "string") return children;
  const parts = children.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
}

// ─── Content Data (plain JS — no JSX) ─────────────────────────────────────────

const sections = [
  {
    title: "What Are Cookies?",
    paragraphs: [
      "Cookies are small text files stored on your device (computer, tablet, or mobile) when you visit a website. They help websites remember information about your visit and can improve your browsing experience.",
    ],
  },
  {
    title: "How We Use Cookies",
    paragraphs: ["We use cookies to:"],
    bullets: [
      "Ensure the website functions properly and loads efficiently.",
      "Remember user preferences (such as language or display preferences).",
      "Understand how visitors interact with our pages so we can improve content and performance.",
      "Measure the effectiveness of campaigns, promotions, and content performance.",
      "Support **relevant travel-related promotions, affiliate offers, and advertisements** where applicable.",
    ],
  },
  {
    title: "Types of Cookies We May Use",
    paragraphs: ["Cookies on our site may include:"],
    bullets: [
      "**Essential Cookies:** required for core site functionality and security.",
      "**Performance & Analytics Cookies:** help us understand traffic and usage patterns (e.g., pages visited, time on site).",
      "**Functionality Cookies:** remember choices you make to provide a more personalized experience.",
      "**Advertising & Affiliate Cookies:** may be used by advertising or affiliate partners to measure conversions or show relevant promotions.",
    ],
  },
  {
    title: "Third-Party Cookies",
    paragraphs: [
      "Some cookies may be set by third-party services we use, such as analytics providers, embedded media, or affiliate partners. These third parties may collect information in accordance with their own privacy policies.",
    ],
  },
  {
    title: "Managing or Disabling Cookies",
    paragraphs: [
      "You can control or disable cookies through your browser settings. Most browsers allow you to refuse cookies or delete existing cookies.",
    ],
    bullets: [
      "Disabling cookies may affect site functionality and the user experience.",
      "If you use multiple devices or browsers, you may need to update your cookie settings in each one.",
    ],
  },
  {
    title: "Updates to This Cookies Policy",
    paragraphs: [
      "We may update or modify this Cookies Policy at any time. Any changes will be posted on this page with the revised date. Please review this policy periodically to stay informed.",
    ],
    extraParagraphs: ["**Last Updated: 15.05.2026**"],
  },
  {
    title: "Contact Us",
    paragraphs: [
      "If you have any questions or concerns about this Cookies Policy, please reach out to us at:",
    ],
    contactLines: [
      { label: "Legal Entity", value: "GD Brand Creative" },
      { label: "Brand Name", value: "Travel Rethink Ways" },
      { label: "Email", value: "travel@rethinkways.com", href: "mailto:travel@rethinkways.com" },
    ],
  },
];

// ─── Style helpers ────────────────────────────────────────────────────────────

const textPrimary = (dark) => ({ color: dark ? "#ffffff" : "rgb(13, 19, 23)" });
const textMuted = (dark) => ({ color: dark ? "rgba(255,255,255,0.82)" : "rgba(13,19,23,0.76)" });

// ─── Component ───────────────────────────────────────────────────────────────

export default function CookiesPolicy() {
  const isDark = useSyncedDarkMode();
  const container = { width: "100%", maxWidth: 1500, padding: "0 24px", margin: "0 auto" };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark ? "#000000" : "#ffffff",
        color: isDark ? "#ffffff" : "rgb(13, 19, 23)",
        fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
        WebkitFontSmoothing: "antialiased",
        textRendering: "optimizeLegibility",
        transition: "background 200ms, color 200ms",
      }}
    >
      {/* Inject fonts scoped to this component */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Yeseva+One&display=swap');
        .trw-cp-h1 {
          font-family: 'Yeseva One', Georgia, 'Times New Roman', serif !important;
          font-weight: 400 !important;
        }
      `}</style>

      <main style={{ padding: "40px 0 0" }}>
        {/* ── Hero ── */}
        <section style={container}>
          <h1
            className="trw-cp-h1"
            style={{
              margin: 0,
              fontFamily: "'Yeseva One', Georgia, 'Times New Roman', serif",
              fontWeight: 400,
              fontSize: "41px",
              lineHeight: "51px",
              letterSpacing: 0,
              ...textPrimary(isDark),
            }}
          >
            Cookies Policy
          </h1>

          <p
            style={{
              margin: "22px 0 0",
              fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
              fontWeight: 600,
              fontSize: "18px",
              lineHeight: "29px",
              ...textPrimary(isDark),
            }}
          >
            <strong style={{ fontWeight: 600 }}>Effective Date:</strong> 15.05.2026
          </p>

          <div style={{ marginTop: "20px", display: "grid", gap: "22px", marginBottom: "36px" }}>
            {[
              "This Cookies Policy explains how **www.Travel.Rethinkways.com** (\"we,\" \"our,\" or \"us\") uses cookies and similar technologies when you visit or interact with our website.",
              "**Travel.Rethinkways.com** is powered by and legally operated by **GD Brand Creative**, which is responsible for managing this website and related services.",
              "By continuing to browse or use this site, you agree to the use of cookies as described in this policy. You can manage cookies at any time via your browser settings.",
            ].map((line, i) => (
              <p
                key={i}
                style={{
                  margin: 0,
                  fontWeight: 400,
                  fontSize: "17px",
                  lineHeight: "30px",
                  ...textMuted(isDark),
                }}
              >
                <Rich>{line}</Rich>
              </p>
            ))}
          </div>
        </section>

        {/* ── Article Sections ── */}
        <article style={container}>
          {sections.map((section, idx) => (
            <section
              key={idx}
              style={{
                borderTop: 0,
                marginTop: idx === 0 ? 0 : "36px",
              }}
            >
              {section.title && (
                <h2
                  style={{
                    margin: "0 0 12px",
                    fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
                    fontWeight: 600,
                    fontSize: "28px",
                    lineHeight: "39px",
                    ...textPrimary(isDark),
                  }}
                >
                  {section.title}
                </h2>
              )}

              {section.paragraphs &&
                section.paragraphs.map((para, i) => (
                  <p key={i} style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}>
                    <Rich>{para}</Rich>
                  </p>
                ))}

              {section.bullets && section.bullets.length > 0 && (
                <ul style={{ margin: "8px 0 0 38px", padding: 0, listStyle: "disc" }}>
                  {section.bullets.map((bullet, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: "17px",
                        lineHeight: "30px",
                        marginTop: i > 0 ? "2px" : 0,
                        ...textMuted(isDark),
                      }}
                    >
                      <Rich>{bullet}</Rich>
                    </li>
                  ))}
                </ul>
              )}

              {section.extraParagraphs &&
                section.extraParagraphs.map((para, i) => (
                  <p
                    key={i}
                    style={{
                      margin: "10px 0 0",
                      fontSize: "17px",
                      lineHeight: "30px",
                      ...textMuted(isDark),
                    }}
                  >
                    <Rich>{para}</Rich>
                  </p>
                ))}

              {section.contactLines && section.contactLines.length > 0 && (
                <div style={{ marginTop: "8px" }}>
                  {section.contactLines.map((line, i) => (
                    <p key={i} style={{ margin: 0, fontSize: "17px", lineHeight: "30px", ...textMuted(isDark) }}>
                      <strong style={{ fontWeight: 600, ...textPrimary(isDark) }}>{line.label}:</strong>{" "}
                      {line.href ? (
                        <a
                          href={line.href}
                          style={{
                            textDecoration: "underline",
                            textUnderlineOffset: "3px",
                            ...textPrimary(isDark),
                          }}
                        >
                          {line.value}
                        </a>
                      ) : (
                        line.value
                      )}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </article>
      </main>
    </div>
  );
}

